/**
 * Linear's glyphs, redrawn for the monitor: status circles, priority bars,
 * people, project health, controls and activity. Geometry and colours follow
 * linear.app's own list so the publications list and the frameworks table
 * read as one surface.
 *
 * @module components/regulation/monitor/linearGlyphs
 */
import type { ReactNode } from "react";

import { avatarGradient } from "~/lib/avatarGradient";
import { cn } from "~/lib/utils";
import type { RegulatoryUpdate } from "../../../state/policies";
import { Avatar, AvatarFallback, personInitials } from "../../ui/avatar";
import type { Health } from "./frameworkRollup";
import type { ImpactLevel, PublicationState } from "./publications";

/**
 * Linear's workflow colours: in progress is yellow, done is indigo, health is
 * green / yellow / red, the rest are the tertiary text colour. Kept to
 * Linear's so the monitor reads as one surface.
 */
export const LINEAR_YELLOW = "#f0bf00";
export const LINEAR_INDIGO = "#5e6ad2";
export const LINEAR_GREEN = "#4cb782";
export const LINEAR_RED = "#eb5757";

/** Linear's 14px status circle, geometry for geometry; the fill says how far along it is. */
export function StatusIcon({
  state,
  className,
  strokeWidth = 1.5,
}: {
  readonly state: PublicationState;
  readonly className?: string;
  /** Linear's 1.5 by default; 1 beside 1px line icons, as on Home. */
  readonly strokeWidth?: number;
}) {
  const ring = (color: string, extra?: ReactNode) => (
    <svg viewBox="0 0 14 14" aria-hidden className={cn("size-3.5 shrink-0", className)}>
      <rect x="1" y="1" width="12" height="12" rx="6" stroke={color} strokeWidth={strokeWidth} fill="none" />
      {extra}
    </svg>
  );
  const disc = (color: string, mark: string) => (
    <svg viewBox="0 0 14 14" aria-hidden className={cn("size-3.5 shrink-0", className)}>
      <circle cx="7" cy="7" r="7" fill={color} />
      <path d={mark} fill="none" stroke="var(--color-background)" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  switch (state) {
    case "findings":
      return ring(
        LINEAR_YELLOW,
        <path fill={LINEAR_YELLOW} d="M 3.5,3.5 L3.5,0 A3.5,3.5 0 0,1 3.5, 7 z" transform="translate(3.5,3.5)" />,
      );
    case "decide":
      return ring("var(--color-muted-foreground)");
    case "handled":
      return disc(LINEAR_INDIGO, "M4.3 7.2 6.1 9 9.7 5.3");
    case "not_applicable":
      return disc("var(--color-muted-foreground)", "M5.1 5.1 8.9 8.9 M8.9 5.1 5.1 8.9");
    case "proposed_not_applicable":
      return (
        <svg viewBox="0 0 14 14" aria-hidden className={cn("size-3.5 shrink-0", className)}>
          <circle cx="7" cy="7" r="6" fill="none" stroke="var(--color-muted-foreground)" strokeWidth={strokeWidth} strokeDasharray="1.4 1.74" strokeDashoffset="0.65" />
        </svg>
      );
  }
}

/** Linear's 16px priority glyphs: the orange square for urgent, three bars otherwise. */
export function ImpactIcon({ level }: { readonly level: ImpactLevel }) {
  if (level === "urgent") {
    return (
      <svg viewBox="0 0 16 16" aria-hidden className="size-4 shrink-0" style={{ color: "oklch(0.7 0.18 45)" }}>
        <path
          fill="currentColor"
          d="M3 1C1.91 1 1 1.91 1 3v10c0 1.09.91 2 2 2h10c1.09 0 2-.91 2-2V3c0-1.09-.91-2-2-2H3Zm4 3h2l-.25 5.5h-1.5L7 4Zm2 7.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z"
        />
      </svg>
    );
  }
  if (level === "none") {
    return (
      <svg viewBox="0 0 16 16" aria-hidden className="size-4 shrink-0 fill-muted-foreground">
        <rect x="1.5" y="7.25" width="3" height="1.5" rx="0.5" opacity="0.9" />
        <rect x="6.5" y="7.25" width="3" height="1.5" rx="0.5" opacity="0.9" />
        <rect x="11.5" y="7.25" width="3" height="1.5" rx="0.5" opacity="0.9" />
      </svg>
    );
  }
  const lit = level === "high" ? 3 : 2;
  return (
    <svg viewBox="0 0 16 16" aria-hidden className="size-4 shrink-0 fill-muted-foreground">
      <rect x="1.5" y="8" width="3" height="6" rx="1" />
      <rect x="6.5" y="5" width="3" height="9" rx="1" fillOpacity={lit >= 2 ? 1 : 0.4} />
      <rect x="11.5" y="2" width="3" height="12" rx="1" fillOpacity={lit >= 3 ? 1 : 0.4} />
    </svg>
  );
}

/** Who has the publication's word: the person who decided, the agent's proposal, or nobody yet. */
export function DeciderAvatar({ update }: { readonly update: RegulatoryUpdate }) {
  const email = update.applicabilityDecidedByEmail;
  if (email) return <PersonGlyph email={email} title={`Decided by ${email}`} />;
  if ((update.applicability ?? "needs_review") !== "needs_review") {
    return (
      <span title="Proposed by the agent" className="flex size-4 items-center justify-center rounded-full bg-foreground">
        <img src="/amoreg-logo.svg" alt="" className="size-2.5 invert dark:invert-0" />
      </span>
    );
  }
  return <PersonGlyph email={null} title="Nobody has decided" />;
}

/** A person as Linear draws an assignee: gradient initials, or the dashed "unassigned" figure. */
export function PersonGlyph({
  email,
  title,
  size = "sm",
}: {
  readonly email: string | null;
  readonly title: string;
  readonly size?: "sm" | "md";
}) {
  const box = size === "md" ? "size-5 text-[8px]" : "size-4 text-[7px]";
  if (email) {
    return (
      <Avatar title={title} className={cn(box, "font-semibold")}>
        <AvatarFallback className="text-white" style={{ backgroundImage: avatarGradient(email) }}>
          {personInitials({ email })}
        </AvatarFallback>
      </Avatar>
    );
  }
  return (
    <svg viewBox="0 0 16 16" role="img" aria-label={title} className={cn(box, "shrink-0 text-muted-foreground")}>
      <title>{title}</title>
      <circle cx="8" cy="8" r="7.25" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="2 1.6" />
      <circle cx="8" cy="6.4" r="2" fill="currentColor" />
      <path d="M4.6 11.6c.6-1.6 1.9-2.5 3.4-2.5s2.8.9 3.4 2.5" fill="currentColor" />
    </svg>
  );
}

/** Linear's project health: a tinted disc with a pulse line, then the word. */
export const HEALTH: Record<Health, { readonly label: string; readonly color: string }> = {
  on_track: { label: "On track", color: LINEAR_GREEN },
  at_risk: { label: "At risk", color: LINEAR_YELLOW },
  off_track: { label: "Off track", color: LINEAR_RED },
};
export function HealthIcon({ health }: { readonly health: Health }) {
  const { color } = HEALTH[health];
  return (
    <svg viewBox="0 0 16 16" aria-hidden className="size-4 shrink-0">
      <circle cx="8" cy="8" r="8" fill={color} fillOpacity="0.16" />
      <path
        d={health === "off_track" ? "M4 6.5 6.5 9 9 6.5 12 9.5" : "M4 9.5 6.5 7 9 9.5 12 6.5"}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Linear's hexagon-check, standing for controls addressed out of controls held. */
export function ControlsIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden className="size-4 shrink-0" style={{ color: LINEAR_INDIGO }}>
      <path d="M8 1.5 13.6 4.75v6.5L8 14.5 2.4 11.25v-6.5Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M5.6 8.1 7.3 9.7 10.4 6.4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Linear's activity chevrons: one to three, stacked, for how busy it has been lately. */
export function ActivityIcon({ level }: { readonly level: 0 | 1 | 2 | 3 }) {
  if (level === 0) return <span className="text-muted-foreground/60">—</span>;
  return (
    <svg viewBox="0 0 16 16" aria-hidden className="size-4 shrink-0" style={{ color: LINEAR_GREEN }}>
      {Array.from({ length: level }, (_, index) => (
        <path
          key={index}
          d={`M3.5 ${10.5 - index * 3} 8 ${6.5 - index * 3} 12.5 ${10.5 - index * 3}`}
          transform={`translate(0 ${(level - 1) * 1.5})`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
